// API Service Layer
// Configure API base URL from environment variable
const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://backend.magnoletienne.fr';

export interface Guest {
  id: number;
  family_id: number;
  first_name: string;
  last_name: string;
  confirmed: boolean | null;
  is_child: boolean;
  age: number | null;
  brunch_invited: boolean;
  brunch_confirmed: boolean | null;
  created_at: string;
}

export interface Family {
  id: number;
  family_name: string;
  unique_link: string;
  contact_email: string | null;
  dietary_restrictions: string | null;
  created_at: string;
}

export interface FamilyWithGuests extends Family {
  guests: Guest[];
}

export interface Statistics {
  totalFamilies: number;
  totalGuests: number;
  confirmedGuests: number;
  declinedGuests: number;
  noResponseGuests: number;
  brunchInvitedGuests: number;
  brunchConfirmedGuests: number;
}

class ApiService {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  /**
   * Search for families by name or guest name
   */
  async searchFamilies(searchTerm: string): Promise<FamilyWithGuests[]> {
    const response = await fetch(`${this.baseUrl}/api/search?q=${encodeURIComponent(searchTerm)}`);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Search failed');
    }

    return data.families;
  }

  /**
   * Get family by unique link
   */
  async getFamilyByLink(uniqueLink: string): Promise<FamilyWithGuests> {
    const response = await fetch(`${this.baseUrl}/api/family/${encodeURIComponent(uniqueLink)}`);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Family not found');
    }

    return data.family;
  }

  /**
   * Get family by ID
   */
  async getFamilyById(familyId: number): Promise<FamilyWithGuests> {
    const response = await fetch(`${this.baseUrl}/api/family/id/${familyId}`);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Family not found');
    }

    return data.family;
  }

  /**
   * Update guest confirmation status
   */
  async updateGuestConfirmation(guestId: number, confirmed: boolean | null): Promise<void> {
    const response = await fetch(`${this.baseUrl}/api/guest/${guestId}/confirm`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ confirmed }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to update confirmation');
    }
  }

  /**
   * Update family dietary restrictions
   */
  async updateFamilyDietaryRestrictions(familyId: number, restrictions: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/api/family/${familyId}/dietary`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ restrictions }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to update dietary restrictions');
    }
  }

  /**
   * Update family contact email
   */
  async updateFamilyContactEmail(familyId: number, email: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/api/family/${familyId}/email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to update email');
    }
  }

  /**
   * Admin: Get all families with guests
   */
  async getAllFamiliesWithGuests(): Promise<FamilyWithGuests[]> {
    const response = await fetch(`${this.baseUrl}/api/admin/families`);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch families');
    }

    return data.families;
  }

  /**
   * Admin: Get statistics
   */
  async getStatistics(): Promise<Statistics> {
    const response = await fetch(`${this.baseUrl}/api/admin/stats`);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch statistics');
    }

    return data.stats;
  }

  /**
   * Admin: Create new family
   */
  async createFamily(familyName: string, uniqueLink: string): Promise<number> {
    const response = await fetch(`${this.baseUrl}/api/admin/family`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ familyName, uniqueLink }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to create family');
    }

    return data.familyId;
  }

  /**
   * Admin: Add guest to family
   */
  async createGuest(familyId: number, firstName: string, lastName: string, isChild: boolean = false): Promise<number> {
    const response = await fetch(`${this.baseUrl}/api/admin/guest`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ familyId, firstName, lastName, isChild }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to create guest');
    }

    return data.guestId;
  }

  /**
   * Admin: Update family
   */
  async updateFamily(familyId: number, familyName: string, uniqueLink: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/api/admin/family/${familyId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ familyName, uniqueLink }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to update family');
    }
  }

  /**
   * Admin: Update guest
   */
  async updateGuest(guestId: number, firstName: string, lastName: string, isChild: boolean = false): Promise<void> {
    const response = await fetch(`${this.baseUrl}/api/admin/guest/${guestId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ firstName, lastName, isChild }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to update guest');
    }
  }

  /**
   * Update guest age
   */
  async updateGuestAge(guestId: number, age: number | null): Promise<void> {
    const response = await fetch(`${this.baseUrl}/api/guest/${guestId}/age`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ age }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to update age');
    }
  }

  /**
   * Admin: Update guest brunch invitation status
   */
  async updateGuestBrunchInvitation(guestId: number, brunchInvited: boolean): Promise<void> {
    const response = await fetch(`${this.baseUrl}/api/admin/guest/${guestId}/brunch-invite`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ brunchInvited }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to update brunch invitation');
    }
  }

  /**
   * Update guest brunch confirmation
   */
  async updateGuestBrunchConfirmation(guestId: number, brunchConfirmed: boolean | null): Promise<void> {
    const response = await fetch(`${this.baseUrl}/api/guest/${guestId}/brunch-confirm`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ brunchConfirmed }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to update brunch confirmation');
    }
  }

  /**
   * Admin: Get all family emails
   */
  async getAllFamilyEmails(): Promise<Array<{id: number, family_name: string, contact_email: string}>> {
    const response = await fetch(`${this.baseUrl}/api/admin/emails`);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch emails');
    }

    return data.emails;
  }
}

// Export a singleton instance
export const api = new ApiService(API_BASE_URL);
